import { useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { MediaUpload } from '@/components/MediaUpload';
import {
  facebookPageService, FbPagePostType, PublishPagePostPayload, PublishPagePostResult,
} from '@/services/facebook-page.service';
import {
  Type, Link2, Image, Images, Video, Clock3, Plus, Trash2, ExternalLink,
  CheckCircle2, AlertCircle, Loader2, CalendarClock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';

type Props = {
  /** Rendered inside a TabbedPage shell — suppress the page Topbar. */
  embedded?: boolean;
};

const TYPES: Array<{ type: FbPagePostType; icon: React.ReactNode; label: string; sub: string }> = [
  { type: 'TEXT', icon: <Type size={20} />, label: 'Text', sub: 'Status update' },
  { type: 'LINK', icon: <Link2 size={20} />, label: 'Link', sub: 'With preview' },
  { type: 'IMAGE', icon: <Image size={20} />, label: 'Image', sub: 'Single photo' },
  { type: 'MULTI_IMAGE', icon: <Images size={20} />, label: 'Multiple images', sub: '2–10 photos' },
  { type: 'VIDEO', icon: <Video size={20} />, label: 'Video', sub: 'Feed video' },
  { type: 'STORY', icon: <Clock3 size={20} />, label: 'Story', sub: '24-hour photo' },
];

export default function PagePublish({ embedded = false }: Props = {}) {
  const [postType, setPostType] = useState<FbPagePostType>('TEXT');
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>(['', '']);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PublishPagePostResult | null>(null);

  const filledImages = imageUrls.filter(Boolean);
  // Stories publish immediately — Meta ignores scheduling on /photo_stories.
  const canSchedule = postType !== 'STORY';

  const ready = (() => {
    if (scheduleOn && canSchedule && !scheduleAt) return false;
    switch (postType) {
      case 'TEXT': return !!message.trim();
      case 'LINK': return !!link.trim();
      case 'IMAGE':
      case 'STORY': return !!imageUrl;
      case 'MULTI_IMAGE': return filledImages.length >= 2;
      case 'VIDEO': return !!videoUrl;
      default: return false;
    }
  })();

  const submit = async () => {
    setBusy(true);
    try {
      const payload: PublishPagePostPayload = { post_type: postType };
      if (message.trim()) payload.message = message.trim();

      if (postType === 'LINK') payload.link = link.trim();
      if (postType === 'IMAGE' || postType === 'STORY') payload.image_url = imageUrl;
      if (postType === 'MULTI_IMAGE') payload.image_urls = filledImages;
      if (postType === 'VIDEO') {
        payload.video_url = videoUrl;
        if (videoTitle.trim()) payload.video_title = videoTitle.trim();
      }
      if (scheduleOn && canSchedule && scheduleAt) {
        payload.scheduled_publish_time = Math.floor(new Date(scheduleAt).getTime() / 1000);
      }

      const r = await facebookPageService.publish(payload);
      setResult(r);
      toast.success(r.scheduled ? 'Scheduled on your Page' : 'Posted to your Page!');
    } catch {
      /* axios interceptor already toasted the backend message */
    } finally { setBusy(false); }
  };

  const reset = () => {
    setResult(null); setMessage(''); setLink(''); setImageUrl('');
    setImageUrls(['', '']); setVideoUrl(''); setVideoTitle('');
    setScheduleOn(false); setScheduleAt('');
  };

  if (result) {
    return (
      <>
        {!embedded && <Topbar title="Publish to Page" breadcrumb="Facebook Ads" />}
        <div className={clsx('w-full space-y-4 px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
          <Card>
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CheckCircle2 size={40} className="text-success" />
              <h3 className="font-display text-lg font-bold text-text-primary">
                {result.scheduled ? 'Scheduled' : 'Published'}
              </h3>
              <p className="text-sm text-text-muted">
                Post <span className="font-mono text-xs">{result.postId}</span> on Page{' '}
                <span className="font-mono text-xs">{result.pageId}</span>
              </p>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                {result.permalink && (
                  <a href={result.permalink} target="_blank" rel="noopener noreferrer">
                    <Button variant="secondary"><ExternalLink size={14} /> View on Facebook</Button>
                  </a>
                )}
                <Button variant="primary" onClick={reset}>Write another</Button>
              </div>
            </div>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      {!embedded && (
        <Topbar
          title="Publish to Page"
          breadcrumb="Facebook Ads"
          description="Post organically to your Facebook Page — not an ad."
        />
      )}

      <div className={clsx('w-full max-w-3xl space-y-4 px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
        {/* Post type */}
        <Card>
          <SectionHeader title="Post type" icon={<Type size={16} />} />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {TYPES.map((t) => (
              <button
                key={t.type}
                onClick={() => setPostType(t.type)}
                className={clsx(
                  'flex flex-col items-start gap-1 rounded-xl border px-3 py-3 text-left transition-all',
                  postType === t.type
                    ? 'border-primary bg-primary-soft'
                    : 'border-border bg-surface hover:border-border-strong hover:bg-surface-alt',
                )}
              >
                <span className={clsx(postType === t.type ? 'text-primary' : 'text-text-muted')}>{t.icon}</span>
                <span className="text-sm font-semibold text-text-primary">{t.label}</span>
                <span className="text-2xs text-text-muted">{t.sub}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Media / link */}
        {postType !== 'TEXT' && (
          <Card>
            <SectionHeader title={postType === 'LINK' ? 'Link' : 'Media'} icon={<Image size={16} />} />

            {postType === 'LINK' && (
              <FormField label="URL" required hint="Facebook builds the preview card from this page's Open Graph tags.">
                <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://slightor.com/offer" />
              </FormField>
            )}

            {(postType === 'IMAGE' || postType === 'STORY') && (
              <FormField
                label={postType === 'STORY' ? 'Story photo' : 'Image'}
                required
                hint={postType === 'STORY'
                  ? 'JPG/PNG · vertical 9:16 works best · disappears after 24 hours'
                  : 'JPG/PNG · up to 4 MB'}
              >
                <MediaUpload mode="image" value={imageUrl} folder="facebook/page" onChange={setImageUrl} />
              </FormField>
            )}

            {postType === 'MULTI_IMAGE' && (
              <div className="space-y-3">
                <p className="text-xs text-text-muted">2–10 photos, shown in this order.</p>
                {imageUrls.map((url, i) => (
                  <div key={i} className="rounded-xl border border-border bg-surface-alt/40 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">
                        Photo {i + 1}
                      </span>
                      {imageUrls.length > 2 && (
                        <button
                          onClick={() => setImageUrls((u) => u.filter((_, idx) => idx !== i))}
                          className="rounded-lg p-1 text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                          title="Remove photo"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    <MediaUpload
                      mode="image"
                      value={url}
                      folder="facebook/page"
                      onChange={(next) => setImageUrls((u) => u.map((v, idx) => (idx === i ? next : v)))}
                    />
                  </div>
                ))}
                {imageUrls.length < 10 && (
                  <Button variant="secondary" onClick={() => setImageUrls((u) => [...u, ''])}>
                    <Plus size={13} /> Add photo
                  </Button>
                )}
                <p className="text-2xs text-text-muted">
                  {filledImages.length} of {imageUrls.length} filled
                  {filledImages.length < 2 && ' — Facebook needs at least 2'}
                </p>
              </div>
            )}

            {postType === 'VIDEO' && (
              <>
                <FormField label="Video" required hint="MP4/MOV · up to 10 GB / 240 min">
                  <MediaUpload mode="video" value={videoUrl} folder="facebook/page" onChange={setVideoUrl} />
                </FormField>
                <FormField label="Title" hint="Optional — shown above the player.">
                  <Input value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} placeholder="Eid sale walkthrough" />
                </FormField>
              </>
            )}
          </Card>
        )}

        {/* Message */}
        {postType !== 'STORY' && (
          <Card>
            <SectionHeader
              title={postType === 'TEXT' ? 'Message' : 'Caption'}
              subtitle={`${message.length.toLocaleString()} / 63,206`}
            />
            <Textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={postType === 'TEXT' ? "What's on your mind?" : 'Optional caption…'}
            />
            {postType === 'TEXT' && !message.trim() && (
              <p className="mt-1 flex items-center gap-1 text-2xs text-text-muted">
                <AlertCircle size={10} /> A text post needs a message.
              </p>
            )}
          </Card>
        )}

        {/* Scheduling */}
        <Card>
          <SectionHeader title="When" icon={<CalendarClock size={16} />} />
          {canSchedule ? (
            <>
              <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-surface-alt px-4 py-3 transition-colors hover:bg-surface">
                <input
                  type="checkbox"
                  checked={scheduleOn}
                  onChange={(e) => setScheduleOn(e.target.checked)}
                  className="h-4 w-4 rounded accent-primary"
                />
                <span className="text-sm font-medium text-text-primary">Schedule for later</span>
                <span className="ml-auto text-xs text-text-muted">Facebook publishes it for you</span>
              </label>
              {scheduleOn && (
                <FormField
                  label="Publish at"
                  required
                  hint="Facebook requires between 10 minutes and 6 months from now."
                >
                  <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
                </FormField>
              )}
            </>
          ) : (
            <p className="text-xs text-text-muted">
              Stories publish immediately — Facebook doesn't support scheduling them.
            </p>
          )}
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button variant="primary" onClick={submit} loading={busy} disabled={!ready || busy}>
            {busy
              ? <><Loader2 size={14} className="animate-spin" /> Publishing…</>
              : scheduleOn && canSchedule ? 'Schedule post' : 'Publish now'}
          </Button>
        </div>
      </div>
    </>
  );
}
